import React, { useState, useEffect } from 'react';
import { css } from '@emotion/react';
import Image from 'next/image';

import { trackWithFingerprint } from '../../../utils/trackWithFingerprint';
import errors from '../../../utils/errors';
import { emailRegex } from '../../../utils/regex';
import { hidden } from '../../../styles/a11y';

import {
  EmailWidgetInput,
  MobileEmailButton,
  StyledParagraph,
  MobileContainer,
  EmailInputWrapper
} from './EmailWidget.styles';
import { Flexbox } from '../../Flexbox';

import {
  DEFAULT_EMAIL_LABEL,
  DEFAULT_EMAIL_BUTTON_TEXT,
  THANKS_TEXT_WITH_SPACING,
  DEFAULT_EMAIL_WIDGET_BUTTON_TEXT,
  SharedProps,
  NonContentfulWidgetProps
} from '../shared';

import subtract from '@/images/subtract.svg';

interface EmailInputProps {
  id?: string;
  placeholder?: string;
  className: string;
  ctaText: string;
  onChange: (val: string) => void;
  options: {};
  onFocus: () => void;
  onBlur: () => void;
  value: string;
  error: boolean;
  success: boolean;
}

interface ButtonProps {
  onClick: () => void;
}

interface Props extends SharedProps {
  ctaText?: string;
  button: (props: ButtonProps) => JSX.Element;
  input: (props: EmailInputProps) => JSX.Element;
}

export const SubmitEmailWidget = ({
  id,
  className,
  placeholderText = DEFAULT_EMAIL_LABEL,
  labelText = DEFAULT_EMAIL_LABEL,
  ctaText = DEFAULT_EMAIL_BUTTON_TEXT,
  button,
  input
}: Props) => {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [isLabelObscured, setIsLabelObscured] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const setFromEvent = () => setMessage('');
    window.addEventListener('mousedown', setFromEvent);

    return () => {
      window.removeEventListener('mousedown', setFromEvent);
    };
  }, []);

  const submit = () => {
    if (!email.match(emailRegex)) {
      setMessage(errors.INVALID_EMAIL);
      return;
    }

    trackWithFingerprint('Lead Capture Form Submitted', {
      email
    });

    setSubmitted(true);
    setEmail('');
  };

  const inputProps: EmailInputProps = {
    id: id,
    placeholder: submitted ? THANKS_TEXT_WITH_SPACING : placeholderText,
    className: 'fs-block',
    ctaText: ctaText,
    onChange: (val: string) => setEmail(val),
    value: email,
    options: {},
    onFocus: () => setIsLabelObscured(true),
    onBlur: () => setIsLabelObscured(false),
    error: !!message,
    success: submitted
  };

  return (
    <MobileContainer
      id="mobile-container"
      data-e2e="email-container"
      className={className}
    >
      <EmailInputWrapper id="input">
        <label
          htmlFor={id}
          css={css`
            ${hidden}
          `}
        >
          {isLabelObscured ? '' : labelText}
        </label>
        {input({ ...inputProps })}
        {message && (
          <Flexbox
            flexDirection="row"
            alignItems="center"
            alignSelf="flex-start"
            marginTop="s"
            marginLeft="s"
          >
            <Image src={subtract} alt="subtract-icon" />
            <StyledParagraph>{message}</StyledParagraph>
          </Flexbox>
        )}
      </EmailInputWrapper>
      {!submitted && button({ onClick: submit })}
    </MobileContainer>
  );
};

export function EmailWidget({
  id,
  className,
  errorPosition = 'bottom',
  labelText = DEFAULT_EMAIL_LABEL,
  placeholderText = DEFAULT_EMAIL_LABEL,
  cfButtonColor = 'ginger',
  cfButtonHoverColor = 'green',
  buttonContent = DEFAULT_EMAIL_WIDGET_BUTTON_TEXT
}: NonContentfulWidgetProps) {
  return (
    <SubmitEmailWidget
      className={className}
      id={id}
      errorPosition={errorPosition}
      labelText={labelText}
      placeholderText={placeholderText}
      input={(props) => (
        <EmailWidgetInput data-e2e="email" data-testid="email" {...props} />
      )}
      button={(props) => (
        <MobileEmailButton
          {...props}
          cfButtonColor={cfButtonColor}
          cfButtonHoverColor={cfButtonHoverColor}
          data-e2e="btn-fail-whale"
          data-testid="btn-fail-whale"
        >
          {buttonContent}
        </MobileEmailButton>
      )}
    />
  );
}
